import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import * as styles from './ActivityItem.module.scss';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import Tooltip from '../components/Tooltip';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';
import getFirstName from '../utils/text/getFirstName';

const ACTIVITY_ITEM_ACTORS_FRAGMENT = gql(/** @complexity 25 */ `
  fragment ActivityItem_Actors on User {
    id
    ...AvatarStack_users
  }
`);

export type ActivityItemIconColor = 'yellow' | 'pink' | 'green';

type ActivityItemProps = {
  icon: string | React.ReactNode;
  iconColor?: ActivityItemIconColor;
  description?: React.ReactNode;
  timestamp?: string | number;
  actors?: DocumentType<typeof ACTIVITY_ITEM_ACTORS_FRAGMENT>[];
  onClick?: () => void;
  href?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

const ActivityItem = ({
  actors,
  icon,
  iconColor,
  description,
  timestamp,
  href,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: ActivityItemProps) => {
  const hasTwoRows = !!timestamp || !!actors?.length;

  const isInteractive = !!onClick || !!href;
  const Component = isInteractive ? Button : 'div';
  return (
    <Component
      className={classNames(styles.activityItem, {
        [styles.isInteractive]: isInteractive,
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      to={href}
    >
      <div className={styles.activityInner}>
        <div className={styles.hoverIndicator} />
        <div className={styles.activityVerticalLine} />
        <div className={styles.activityIcon}>
          {typeof icon === 'string' ? (
            <SVGIcon size={16} className={iconColor && styles[iconColor]}>
              {icon}
            </SVGIcon>
          ) : (
            icon
          )}
        </div>
        <div className={styles.activityFirstRow}>
          {!hasTwoRows && description}
          {hasTwoRows && !!actors?.length && (
            <>
              <span className={styles.activityAvatars}>
                <AvatarStack
                  items={actors}
                  noWrap
                  size={20}
                  limit={3}
                  countOverflowVariant="circle"
                  spacing="tight"
                  borderSize={2}
                />
              </span>
              <span className={styles.activityNames}>
                {actors.map((actor) => getFirstName(actor.name)).join(', ')}
              </span>
            </>
          )}
          {hasTwoRows && !!timestamp && (
            <Tooltip
              asChild
              content={moment(timestamp).format('MMM Do, YYYY, h:mm A')}
            >
              <span className={styles.activityTime}>
                <Timestamp date={timestamp} />
              </span>
            </Tooltip>
          )}
        </div>
        {hasTwoRows && (
          <div className={styles.activitySecondRow}>{description}</div>
        )}
      </div>
    </Component>
  );
};

interface ActivitySeparatorProps {
  children: React.ReactNode;
  onClick?: () => void;
}

export const ActivitySeparator = ({
  children,
  onClick,
}: ActivitySeparatorProps) => {
  return (
    <ActivityItem
      icon={<div className={styles.activitySeparatorIcon} />}
      description={
        <div className={styles.activitySeparatorText}>{children}</div>
      }
      onClick={onClick}
    />
  );
};

export default ActivityItem;
